import {
  ComplexFactory,
  TextValidatorType,
  ValidatorType,
} from "anketa-core/base";
import { Row, Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import { NumericQuestion } from "anketa-core/numeric_question";
import { MultiSelectOptionsQuestion } from "anketa-core/multiselect_options_question";
import { OpenQuestion } from "anketa-core/open_question";
import {
  TextValidator,
  NetworksNoOverlapValidator,
} from "anketa-core/validation";
import { featureSwitchValue } from "src/helper";
import { splitSubnets } from "src/helper/ip";
import { generateAgents, generateProbes } from "../functions";
import { generateKubernetesServers } from "../functions/k3server";
import { generateKula } from "../functions/kula";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
  getTranslation,
  Vpc,
} from "../../../../";
import {
  QuestionsRightsTypes,
  WaveOptionMap,
  SizingOptionMap,
  ServiceModelMap,
} from "..";
import { OptionsQuestion } from "anketa-core/options_question";
import { SuggestionsQuestion } from "anketa-core/suggestions_question";
import { User, Generic, Form } from "anketa-core/complex";
import { ComplexQuestion } from "anketa-core/complex_question";
import { OpsanioWaves } from "../../../";

ComplexFactory.registerClass({
  name: "OpsanioNetworkSelection",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});

// Static DNS records (name / type / ip) for the customer network. The list is
// persisted as a JSON-stringified array by the dedicated DNS-entries editor,
// so this form is only a fallback; it must expose at least one child, though,
// otherwise ComplexQuestion.getForm() rejects it ("Invalid form for complex
// type"). Building it here (rather than reusing Generic, whose form is empty)
// keeps the fields meaningful for the generic modal fallback.
const dnsEntriesForm = (): Form => {
  const form = new Form("OpsanioDnsEntries", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.dns.entries.title",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.dns.entries.title",
      ),
    ),
  ]);
  form.addChild(
    new OpenQuestion("name", [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.network.dns.entries.name",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.network.dns.entries.name",
        ),
      ),
    ]),
  );
  form.addChild(
    new OpenQuestion("type", [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.network.dns.entries.type",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.network.dns.entries.type",
        ),
      ),
    ]),
  );
  form.addChild(
    new OpenQuestion("ip", [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.network.dns.entries.ip",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.network.dns.entries.ip",
        ),
      ),
    ]),
  );
  return form;
};

ComplexFactory.registerClass({
  name: "OpsanioDnsEntries",
  Constructor: Generic as new (init?: any) => Generic,
  form: dnsEntriesForm,
});

/**
 * Default option lists for the "Netzwerk Fragen" firewall / switch
 * multi-selects. Each entry is either a generic vendor ("<Company>") or a
 * concrete device ("<Company> - <Produkt>"). The lists cover the most common
 * vendors/devices worldwide; the questions stay suggestOnly so customers can
 * still create their own entries.
 */
export const FIREWALL_OPTIONS = "default.options.firewalls";
export const SWITCH_OPTIONS = "default.options.switches";

const FIREWALL_DEVICES: string[] = [
  "Palo Alto Networks",
  "Palo Alto Networks - PA-Series",
  "Palo Alto Networks - VM-Series",
  "Palo Alto Networks - Prisma Access",
  "Fortinet",
  "Fortinet - FortiGate",
  "Fortinet - FortiGate VM",
  "Cisco",
  "Cisco - ASA",
  "Cisco - Firepower",
  "Cisco - Meraki MX",
  "Check Point",
  "Check Point - Quantum",
  "Check Point - Maestro",
  "Check Point - CloudGuard",
  "Juniper Networks",
  "Juniper Networks - SRX",
  "Sophos",
  "Sophos - XGS",
  "Sophos - XG",
  "SonicWall",
  "SonicWall - TZ",
  "SonicWall - NSa",
  "WatchGuard",
  "WatchGuard - Firebox",
  "Barracuda",
  "Barracuda - CloudGen Firewall",
  "Zscaler",
  "Zscaler - Internet Access",
  "Forcepoint",
  "Forcepoint - NGFW",
  "Huawei",
  "Huawei - USG",
  "Hillstone Networks",
  "Sangfor",
  "Sangfor - NGAF",
  "Netgate",
  "Netgate - pfSense",
  "OPNsense",
  "Stormshield",
  "Stormshield - SNS",
  "F5 Networks",
  "F5 Networks - BIG-IP",
  "Citrix",
  "Citrix - ADC",
  "Microsoft",
  "Microsoft - Azure Firewall",
  "Cloudflare",
  "Cloudflare - Magic Firewall",
  "Untangle",
];

const SWITCH_DEVICES: string[] = [
  "Cisco",
  "Cisco - Catalyst",
  "Cisco - Nexus",
  "Cisco - Meraki MS",
  "Aruba",
  "Aruba - CX",
  "Aruba - 2930",
  "HPE",
  "HPE - ProCurve",
  "HPE - FlexFabric",
  "Juniper Networks",
  "Juniper Networks - EX",
  "Juniper Networks - QFX",
  "Arista Networks",
  "Arista Networks - 7000 Series",
  "Huawei",
  "Huawei - CloudEngine",
  "Huawei - S Series",
  "Dell",
  "Dell - PowerSwitch",
  "Dell - Force10",
  "Extreme Networks",
  "Extreme Networks - X Series",
  "Netgear",
  "Netgear - ProSafe",
  "Ubiquiti",
  "Ubiquiti - UniFi",
  "MikroTik",
  "MikroTik - CRS",
  "TP-Link",
  "TP-Link - Omada",
  "D-Link",
  "D-Link - DGS",
  "H3C",
  "H3C - S Series",
  "Brocade",
  "Brocade - ICX",
  "Allied Telesis",
  "Allied Telesis - x Series",
  "Fortinet",
  "Fortinet - FortiSwitch",
  "Zyxel",
  "Zyxel - GS Series",
  "Avaya",
  "Linksys",
  "Cumulus Networks",
  "Edgecore Networks",
  "NVIDIA (Mellanox)",
  "NVIDIA (Mellanox) - Spectrum",
  "Ruckus",
];

/** Stable option key (fact value) for a device label; must not contain ".". */
const deviceOptionKey = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** Register a device list as a global option list under the given prefix. */
const registerDeviceOptions = (prefix: string, devices: string[]): void => {
  devices.forEach((label) => {
    MessageCollection.Global.setTranslations(
      `${prefix}.${deviceOptionKey(label)}`,
      [new Translation("en", label), new Translation("de", label)],
    );
  });
};

registerDeviceOptions(FIREWALL_OPTIONS, FIREWALL_DEVICES);
registerDeviceOptions(SWITCH_OPTIONS, SWITCH_DEVICES);

export const opsanioNetworks = (
  oldValues: any,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  setDefaultValues: boolean,
  defaultValues: {
    agents: {
      id: string;
      macs: string[];
      redfishMac: string;
      serialNumber: string;
    }[];
    vpc: { vpcs: Vpc[]; tenantIds: number[] };
    vpcName: string;
    nextWave: OpsanioWaves;
  },
  bgpEnabled: boolean,
  /**
   * "Do you want a two-room solution?" from the LOCATION catalog
   * (cpe.tworoom.tworoomq), passed in rather than re-read, so toggling it
   * updates this section live instead of only after a save and reopen.
   */
  twoRoomQuestion?: OptionsQuestion,
  /**
   * "Own domain and certificate (customer TLD)" from the FEATURES catalog
   * (features.enable.custom_domain). Passed as the question object, not as a
   * boolean, so switching the feature on shows the row immediately instead of
   * only after a save and reopen.
   */
  customDomainQuestion?: OptionsQuestion,
  /**
   * The same switch as it was STORED, read from `oldValues` by the caller. The
   * question object above is unanswered while the catalog is being built - the
   * answers are loaded into it afterwards - so this is what decides whether the
   * row is visible on open, and the question object only takes over once
   * somebody actually toggles it.
   */
  customDomainEnabled: boolean = false,
): Section => {
  const networks = new Section("networks", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.title"),
    ),
  ]);
  questionsRights(networks, {
    isInternal: true,
    isExternal: false,
  });
  networks.help = [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.description"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.description"),
    ),
  ];

  networks.description = [
    new Translation(
      "de",
      getTranslation("en", "anketa.documentation.opsanio.network.help"),
    ),
    new Translation(
      "en",
      getTranslation("de", "anketa.documentation.opsanio.network.help"),
    ),
  ];
  // "Netzwerk Fragen" - pre-sales network inventory questions, relocated here
  // from the former Vertrieb > Netzwerk Fragen sub-tab. Internal-only, matching
  // the rest of the Netzwerk section.
  const networkQuestions = new Section("netzwerk_fragen", [
    new Translation("en", "Netzwerk Fragen"),
    new Translation("de", "Netzwerk Fragen"),
  ]);
  questionsRights(networkQuestions, { isInternal: true, isExternal: false });

  const nfTitle = (s: string) => [
    new Translation("en", s),
    new Translation("de", s),
  ];

  // Firewalls / Switche: multi-select seeded with the most common vendors/
  // devices worldwide ("<Company>" or "<Company> - <Produkt>"). suggestOnly
  // keeps the lists open so customers can still create their own entries.
  const firewalls = new MultiSelectOptionsQuestion(
    "firewalls",
    nfTitle("Firewalls"),
    FIREWALL_OPTIONS,
  );
  firewalls.setSuggestOnly(true);
  questionsRights(firewalls as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: false,
  });
  networkQuestions.addChild(firewalls);

  const switche = new MultiSelectOptionsQuestion(
    "switche",
    nfTitle("Switche"),
    SWITCH_OPTIONS,
  );
  switche.setSuggestOnly(true);
  questionsRights(switche as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: false,
  });
  networkQuestions.addChild(switche);

  const anbindungFcKupfer = new OptionsQuestion(
    "anbindung_fc_kupfer",
    nfTitle("Anbindung FC vs. Kupfer"),
    "default.options.yes_no",
  );
  questionsRights(anbindungFcKupfer, { isInternal: true, isExternal: false });
  networkQuestions.addChild(anbindungFcKupfer);

  const weitereNetzwerkkomponenten = new OpenQuestion(
    "weitere_netzwerkkomponenten",
    nfTitle("Weitere Netzwerkkomponenten"),
  );
  questionsRights(weitereNetzwerkkomponenten, {
    isInternal: true,
    isExternal: false,
  });
  networkQuestions.addChild(weitereNetzwerkkomponenten);

  const vlansZonen = new OptionsQuestion(
    "vlans_zonen",
    nfTitle("Unterschiedliche VLANs / Zonen"),
    "default.options.yes_no",
  );
  questionsRights(vlansZonen, { isInternal: true, isExternal: false });
  networkQuestions.addChild(vlansZonen);

  const flowDaten = new OptionsQuestion(
    "flow_daten",
    nfTitle("Sammlung Flow-Daten"),
    "default.options.yes_no",
  );
  questionsRights(flowDaten, { isInternal: true, isExternal: false });
  networkQuestions.addChild(flowDaten);

  networks.addChild(networkQuestions);

  // Network Row VPC
  const exanioVpcRow = new Row("vpc", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.vpc.titel"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.vpc.titel"),
    ),
  ]);
  questionsRights(exanioVpcRow, {
    isInternal: true,
    isExternal: false,
  });

  exanioVpcRow.help = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.vpc.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.vpc.description",
      ),
    ),
  ];

  const wave = new SuggestionsQuestion(
    "wave",
    [
      new Translation("en", "Deploy wave"),
      new Translation("de", "Deploy wave"),
    ],
    WaveOptionMap.wave,
  );

  questionsRights(wave, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  exanioVpcRow.addChild(wave);

  const serviceModel = new SuggestionsQuestion(
    "service",
    [
      new Translation("en", "Service Modell"),
      new Translation("de", "Service Modell"),
    ],
    ServiceModelMap.service,
  );

  questionsRights(serviceModel, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  exanioVpcRow.addChild(serviceModel);

  const size = new SuggestionsQuestion(
    "size",
    [
      new Translation("en", "OpenSearch sizing"),
      new Translation("de", "OpenSearch sizing"),
    ],
    SizingOptionMap.size,
  );

  questionsRights(size, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  exanioVpcRow.addChild(size);

  const exanioName = new OpenQuestion("vpc_name", [
    new Translation("en", "VPC Name"),
    new Translation("de", "VPC Name"),
  ]);
  questionsRights(exanioName, {
    isInternal: true,
    isExternal: false,
  });
  exanioName.validator = new TextValidator();
  exanioVpcRow.addChild(exanioName);

  const exanioNetworkSelection = new ComplexQuestion(
    "network_vpc",
    [
      new Translation(
        "en",
        getTranslation("en", "anketa.documentation.opsanio.network.title"),
      ),
      new Translation(
        "de",
        getTranslation("de", "anketa.documentation.opsanio.network.title"),
      ),
    ],
    "OpsanioNetworkSelection",
  );
  exanioVpcRow.addChild(exanioNetworkSelection);
  questionsRights(exanioNetworkSelection, {
    isInternal: true,
    isExternal: false,
  });

  networks.addChild(exanioVpcRow);

  const exanioCloudRow = new Row("exanio_cloud", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.exanio.titel"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.exanio.titel"),
    ),
  ]);
  questionsRights(exanioCloudRow, {
    isInternal: true,
    isExternal: false,
  });
  exanioCloudRow.description = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.exanio.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.exanio.description",
      ),
    ),
  ];

  const exanioCloudNetwork = new OpenQuestion("exanio_cloud_network", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.title"),
    ),
  ]);
  questionsRights(exanioCloudNetwork, {
    isInternal: true,
    isExternal: false,
  });
  exanioCloudRow.addChild(exanioCloudNetwork);
  exanioCloudNetwork.readonly = true;

  const exanioCloudVlan = new NumericQuestion("exanio_cloud_vlan", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.vlan.title"),
    ),
    new Translation(
      "en",
      getTranslation("de", "anketa.documentation.opsanio.vlan.title"),
    ),
  ]);
  questionsRights(exanioCloudVlan, {
    isInternal: true,
    isExternal: false,
  });
  exanioCloudVlan.readonly = true;
  exanioCloudRow.addChild(exanioCloudVlan);

  const opsanioAgents = new NumericQuestion("number_of_agents", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.agent.count"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.agent.count"),
    ),
  ]);
  questionsRights(opsanioAgents, {
    isInternal: true,
    isExternal: false,
  });

  exanioCloudRow.addChild(opsanioAgents);

  const opsanioUpdateServer = new NumericQuestion("number_of_master", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.server.count"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.server.count"),
    ),
  ]);
  questionsRights(opsanioUpdateServer, {
    isInternal: true,
    isExternal: false,
  });

  exanioCloudRow.addChild(opsanioUpdateServer);

  const opsanioProbes = new NumericQuestion("number_of_probes", [
    new Translation("en", "Number of Probes"),
    new Translation("de", "Anzahl der Probes"),
  ]);
  questionsRights(opsanioProbes, {
    isInternal: true,
    isExternal: false,
  });

  exanioCloudRow.addChild(opsanioProbes);

  networks.addChild(exanioCloudRow);

  // Network Row Transfer Network
  const transferRow = new Row("transfer", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.transfer.title",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.transfer.title",
      ),
    ),
  ]);
  questionsRights(transferRow, {
    isInternal: true,
    isExternal: false,
  });

  transferRow.description = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.transfer.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.transfer.description",
      ),
    ),
  ];

  const transferNetwork = new OpenQuestion("transfer_network", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.title"),
    ),
  ]);
  questionsRights(transferNetwork, {
    isInternal: true,
    isExternal: false,
  });

  transferRow.addChild(transferNetwork);
  transferNetwork.readonly = true;

  const transferVlan = new NumericQuestion("transfer_vlan", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.vlan.title"),
    ),
    new Translation(
      "en",
      getTranslation("de", "anketa.documentation.opsanio.vlan.title"),
    ),
  ]);
  questionsRights(transferVlan, {
    isInternal: true,
    isExternal: false,
  });

  transferVlan.readonly = true;
  transferRow.addChild(transferVlan);

  networks.addChild(transferRow);

  const wireguardRow = new Row("wireguard", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.wireguard.title",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.wireguard.title",
      ),
    ),
  ]);
  questionsRights(wireguardRow, {
    isInternal: true,
    isExternal: false,
  });

  wireguardRow.description = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.wireguard.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.wireguard.description",
      ),
    ),
  ];
  const wireguardPublicIp = new OpenQuestion("wireguard_ip", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.wireguard.public.ip",
      ),
    ),
    new Translation(
      "en",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.wireguard.public.ip",
      ),
    ),
  ]);
  questionsRights(wireguardPublicIp, {
    isInternal: true,
    isExternal: false,
  });

  wireguardPublicIp.validator = new TextValidator(TextValidatorType.IP);
  wireguardPublicIp.setFactValue("88.205.28.70");
  wireguardRow.addChild(wireguardPublicIp);

  const wireguardDNS = new OpenQuestion("wireguard_dns", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.wireguard.public.dns",
      ),
    ),
    new Translation(
      "en",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.wireguard.public.dns",
      ),
    ),
  ]);
  questionsRights(wireguardDNS, {
    isInternal: true,
    isExternal: false,
  });
  wireguardDNS.readonly = true;
  wireguardRow.addChild(wireguardDNS);

  const wireguardPort = new OpenQuestion("wireguard_port", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.wireguard.public.port",
      ),
    ),
    new Translation(
      "en",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.wireguard.public.port",
      ),
    ),
  ]);
  questionsRights(wireguardDNS, {
    isInternal: true,
    isExternal: false,
  });

  wireguardPort.readonly = true;
  wireguardRow.addChild(wireguardPort);

  networks.addChild(wireguardRow);

  // Network Row CPE
  const cpeRow = new Row("cpe_network", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.cpe.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.cpe.title"),
    ),
  ]);
  questionsRights(cpeRow, {
    isInternal: true,
    isExternal: false,
  });

  cpeRow.description = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.cpe.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.cpe.description",
      ),
    ),
  ];

  const isVm = new OptionsQuestion(
    "isvm",
    [
      new Translation(
        "en",
        getTranslation("en", "anketa.documentation.opsanio.common.rack.is.vm"),
      ),
      new Translation(
        "de",
        getTranslation("de", "anketa.documentation.opsanio.common.rack.is.vm"),
      ),
    ],
    "default.options.yes_no",
  );
  questionsRights(isVm, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  isVm.required = true;
  cpeRow.addChild(isVm);

  const useUefi = new OptionsQuestion(
    "useuefi",
    [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.common.rack.use.uefi",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.common.rack.use.uefi",
        ),
      ),
    ],
    "default.options.yes_no",
  );
  questionsRights(useUefi, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  useUefi.required = true;

  cpeRow.addChild(useUefi);

  const cpeNetwork = new OpenQuestion("cpe_network_network", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.title"),
    ),
  ]);
  questionsRights(cpeNetwork, {
    isInternal: true,
    isExternal: false,
  });

  cpeRow.addChild(cpeNetwork);
  cpeNetwork.readonly = true;

  const cpeVlan = new NumericQuestion("cpe_network_vlan", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.vlan.title"),
    ),
    new Translation(
      "en",
      getTranslation("de", "anketa.documentation.opsanio.vlan.title"),
    ),
  ]);
  questionsRights(cpeVlan, {
    isInternal: true,
    isExternal: false,
  });

  cpeVlan.setFactValue("3940");
  cpeRow.addChild(cpeVlan);

  networks.addChild(cpeRow);

  const cpeNetworkA = new OpenQuestion("cpe_network_network_a", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.cpe-a.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.cpe-a.title"),
    ),
  ]);
  questionsRights(cpeNetworkA, {
    isInternal: true,
    isExternal: false,
  });

  cpeRow.addChild(cpeNetworkA);
  cpeNetworkA.readonly = true;

  const cpeNetworkB = new OpenQuestion("cpe_network_network_b", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.cpe-a.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.cpe-a.title"),
    ),
  ]);
  questionsRights(cpeNetworkB, {
    isInternal: true,
    isExternal: false,
  });

  cpeRow.addChild(cpeNetworkB);
  cpeNetworkB.readonly = true;

  // Cutsomer Side Network
  const customerNetwork = new Row("customer", [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.customer.title",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.customer.title",
      ),
    ),
  ]);
  questionsRights(customerNetwork, {
    isInternal: true,
    isExternal: false,
  });

  customerNetwork.description = [
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.customer.description",
      ).replace(
        "{{hostCount}}",
        parseInt(String(opsanioAgents.fact.raw), 10)
          ? (parseInt(String(opsanioAgents.fact.raw), 10) + 1).toString()
          : "5",
      ),
    ),
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.customer.description",
      ).replaceAll(
        "{{hostCount}}",
        parseInt(String(opsanioAgents.fact.raw), 10)
          ? (parseInt(String(opsanioAgents.fact.raw), 10) + 1).toString()
          : "5",
      ),
    ),
  ];
  networks.addChild(customerNetwork);

  const customerDashboardSettings = new Row("customer_settings", [
    new Translation(
      "en",
      getTranslation("en", "anketa.opsanio.customer.dashboard"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.opsanio.customer.dashboard"),
    ),
  ]);
  // Publishing the dashboards under the customer's own name is a DOMAIN swap,
  // not a single hostname: every ingress host `<service>.<vpc>.exanio.net` is
  // rewritten to `<service>.<domain>`, Traefik matches routers on that name and
  // its header middlewares (allowedHosts, accessControlAllowOriginList,
  // frame-ancestors) are an allowlist keyed by it - a host that is routed but
  // not listed answers 500 "Bad Host" without ever reaching a backend.
  // So the answer has to be the bare domain: no scheme, no path, no `*.`
  // prefix, and the certificate has to cover `*.<domain>`.
  // Certificate and password go through Password Pusher, never by mail.
  customerDashboardSettings.description = [
    new Translation(
      "en",
      getTranslation("en", "anketa.opsanio.customer.dashboard.description"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.opsanio.customer.dashboard.description"),
    ),
  ];
  networks.addChild(customerDashboardSettings);

  const customerDnsName = new OpenQuestion("customer_dns_name", [
    new Translation(
      "en",
      getTranslation("en", "anketa.opsanio.customer.dashboard.dns"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.opsanio.customer.dashboard.dns"),
    ),
  ]);
  customerDashboardSettings.addChild(customerDnsName);
  customerDnsName.help = [
    new Translation(
      "en",
      getTranslation("en", "anketa.opsanio.customer.dashboard.dns.help"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.opsanio.customer.dashboard.dns.help"),
    ),
  ];
  const customerDnsNameValidator = new TextValidator();
  // Bare DNS domain, at least two labels, RFC 1123 labels (no leading/trailing
  // hyphen). Both cases are accepted and lowercased on the way out - the empty
  // alternative keeps the question optional, and empty means "stay on
  // <vpc>.exanio.net". Written without lookbehind on purpose: the regex is
  // shipped to the browser as a literal and is also round-tripped through
  // TextValidator.save(), which keeps only `source` and drops any flags.
  customerDnsNameValidator.pattern =
    /^$|^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  customerDnsNameValidator.maxLength = 253;
  customerDnsName.validator = customerDnsNameValidator;

  // The whole row hangs off the feature switch: a cluster that stays on
  // <vpc>.exanio.net has no customer domain to name and no certificate to hand
  // over, and an answer left behind here is not emitted either (the parser
  // reads the same switch). Hidden when the feature question cannot be found at
  // all - an unanswered switch is a No.
  const applyCustomDomainVisibility = (): void => {
    // Unanswered falls through to the stored answer, which is the state the
    // questionnaire opens in; the question only takes over once it is toggled.
    const enabled = featureSwitchValue(
      customDomainQuestion?.fact.raw?.toString(),
      customDomainEnabled,
    );
    customerDashboardSettings.visible = enabled;
    customerDnsName.visible = enabled;
  };
  applyCustomDomainVisibility();
  customDomainQuestion?.addChangeListener(applyCustomDomainVisibility);

  const customerNetworkCidr = new OpenQuestion("customer_cidr", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.cidr.title"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.cidr.title"),
    ),
  ]);
  questionsRights(customerNetworkCidr);

  const customerCidrValidator = new NetworksNoOverlapValidator(
    ValidatorType.IpComparison,
  );
  // Name the field in the error ("<title> is not a valid IPv4 network CIDR")
  // instead of the generic default.
  customerCidrValidator.cidrName = customerNetworkCidr.title;
  customerNetworkCidr.validator = customerCidrValidator;

  /*   customerNetworkCidr.addChangeListener(() => {
    const vpc: Vpc | undefined = JSON.parse(exanioNetworkSelection);
    if (vpc) {
      const exanioCidr = vpc.cidr;
      const customerCidr = customerNetworkCidr.fact.raw;
      if (
        exanioCidr !== undefined &&
        customerCidr !== undefined &&
        customerNetworkCidr.validator !== undefined
      ) {
        const validator =
          customerNetworkCidr.validator as NetworksNoOverlapValidator;
        validator.cidr = exanioCidr.toString();
        validator.compareWith = customerCidr.toString();
        validator.cidrName = customerNetwork.title;
        validator.compareWithName = exanioNetwork.title;
      }
    }
  }); */

  customerNetwork.addChild(customerNetworkCidr);

  const customerNetworkVlan = new OpenQuestion("customer_vlan", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.vlan.title"),
    ),
    new Translation(
      "en",
      getTranslation("de", "anketa.documentation.opsanio.vlan.title"),
    ),
  ]);
  questionsRights(customerNetworkVlan);

  customerNetworkVlan.validator = new TextValidator(TextValidatorType.VLAN);
  customerNetwork.addChild(customerNetworkVlan);

  const customerNetworkGw = new OpenQuestion("customer_gw", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.gateway"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.gateway"),
    ),
  ]);
  questionsRights(customerNetworkGw);

  customerNetworkGw.validator = new TextValidator(TextValidatorType.IP);
  customerNetwork.addChild(customerNetworkGw);

  // --- Room B (two-room sites) ---------------------------------------------
  //
  // A two-room site splits the CUSTOMER network and nothing else: each room has
  // its own customer CIDR and, usually, its own VLAN, and a node is reachable
  // only on the one it is cabled to. CPE, transfer and exanio stay cluster-wide
  // - they are the internal fabric, and splitting exanio would ripple into
  // etcd peering, tlsSan and the WireGuard allowed-ips.
  //
  // The fields above are room A. These three are room B, and they are shown
  // only when "Unterschiedliche VLANs / Zonen" is Yes. That question already
  // existed and was read by nothing; this is what now consumes it.
  //
  // Each node is then assigned a room by `agent_<n>_zone`, and the parser emits
  // both rooms as `networks.customer.subnets[]` keyed by `zone`.
  const customerNetworkCidrB = new OpenQuestion("customer_cidr_b", [
    new Translation("en", "Customer network room B (CIDR)"),
    new Translation("de", "Kundennetz Raum B (CIDR)"),
  ]);
  questionsRights(customerNetworkCidrB);
  customerNetworkCidrB.validator = new TextValidator(TextValidatorType.CIDR);
  customerNetwork.addChild(customerNetworkCidrB);

  const customerNetworkVlanB = new OpenQuestion("customer_vlan_b", [
    new Translation("en", "Customer VLAN room B"),
    new Translation("de", "Kunden VLAN Raum B"),
  ]);
  questionsRights(customerNetworkVlanB);
  customerNetworkVlanB.validator = new TextValidator(TextValidatorType.VLAN);
  // Left blank deliberately rather than defaulted: blank means "same VLAN id as
  // room A", which is a real topology (one L2 stretched across both rooms with
  // two subnets on it). A default of 0 would be worse than blank - 0 is a
  // reserved id and would render an unusable interface.
  customerNetworkVlanB.help = [
    new Translation(
      "en",
      "Leave empty if room B is on the same VLAN id as room A and only the subnet differs.",
    ),
    new Translation(
      "de",
      "Leer lassen, wenn Raum B dieselbe VLAN-ID wie Raum A nutzt und sich nur das Subnetz unterscheidet.",
    ),
  ];
  customerNetwork.addChild(customerNetworkVlanB);

  const customerNetworkGwB = new OpenQuestion("customer_gw_b", [
    new Translation("en", "Customer gateway room B"),
    new Translation("de", "Kunden Gateway Raum B"),
  ]);
  questionsRights(customerNetworkGwB);
  customerNetworkGwB.validator = new TextValidator(TextValidatorType.IP);
  customerNetworkGwB.help = [
    new Translation("en", "Leave empty to reuse room A's gateway."),
    new Translation(
      "de",
      "Leer lassen, um das Gateway von Raum A zu verwenden.",
    ),
  ];
  customerNetwork.addChild(customerNetworkGwB);

  // Rooms in separate fire compartments usually run their own local resolver
  // and their own time source, so these are per-room like the gateway. Blank
  // inherits room A, which is what a site that only differs in placement wants.
  const customerNetworkDnsB = new OpenQuestion("customer_dns_b", [
    new Translation("en", "Customer DNS server room B"),
    new Translation("de", "Kunden DNS-Server Raum B"),
  ]);
  questionsRights(customerNetworkDnsB);
  customerNetworkDnsB.validator = new TextValidator(TextValidatorType.IP);
  customerNetworkDnsB.help = [
    new Translation("en", "Leave empty to reuse room A's DNS server."),
    new Translation(
      "de",
      "Leer lassen, um den DNS-Server von Raum A zu verwenden.",
    ),
  ];
  customerNetwork.addChild(customerNetworkDnsB);

  const customerNetworkNtpB = new OpenQuestion("customer_ntp_b", [
    new Translation("en", "Customer NTP server room B"),
    new Translation("de", "Kunden NTP-Server Raum B"),
  ]);
  questionsRights(customerNetworkNtpB);
  customerNetworkNtpB.validator = new TextValidator(TextValidatorType.IP);
  customerNetworkNtpB.help = [
    new Translation("en", "Leave empty to reuse room A's NTP server."),
    new Translation(
      "de",
      "Leer lassen, um den NTP-Server von Raum A zu verwenden.",
    ),
  ];
  customerNetwork.addChild(customerNetworkNtpB);

  // Two gates, and they answer different questions.
  //
  // The SITE fact - "Do you want a two-room solution?" - lives in the location
  // catalog and is what decides whether a node can be assigned to a room at
  // all. NOT networks.netzwerk_fragen.vlans_zonen: that is a general
  // "different VLANs / zones" question about the customer's own estate and has
  // nothing to say about where our appliances stand.
  //
  // The NETWORK fact - customerZoned, just below - decides whether the two
  // rooms have different customer networks. Two rooms on one stretched L2 share
  // a CIDR and a VLAN and only the physical placement differs, which is common
  // enough that assuming otherwise would invent a subnet nobody asked for.
  const isTwoRoomSite = (): boolean =>
    twoRoomQuestion !== undefined && twoRoomQuestion.fact.raw === "0";

  const customerZoned = new OptionsQuestion(
    "customer_zoned",
    [
      new Translation("en", "Different customer network per room"),
      new Translation("de", "Unterschiedliche Kundennetze je Raum"),
    ],
    "default.options.yes_no",
  );
  customerZoned.help = [
    new Translation(
      "en",
      "Yes if room A and room B have different customer CIDRs or VLANs. No if both rooms share one customer network and only the physical placement differs.",
    ),
    new Translation(
      "de",
      "Ja, wenn Raum A und Raum B unterschiedliche Kunden-CIDRs oder VLANs haben. Nein, wenn beide Räume dasselbe Kundennetz nutzen und sich nur die Aufstellung unterscheidet.",
    ),
  ];
  questionsRights(customerZoned, { isInternal: true, isExternal: false });
  customerNetwork.addChild(customerZoned);

  const applyZoneVisibility = (): void => {
    const twoRoom = isTwoRoomSite();
    // The "different networks?" question only makes sense once there are two
    // rooms to compare.
    customerZoned.visible = twoRoom;
    const split = twoRoom && customerZoned.fact.raw === "0";
    customerNetworkCidrB.visible = split;
    customerNetworkVlanB.visible = split;
    customerNetworkGwB.visible = split;
    customerNetworkDnsB.visible = split;
    customerNetworkNtpB.visible = split;

    // Every agent's room question, wherever it currently sits.
    //
    // Walked rather than held in a list because the agent rows are rebuilt from
    // several places - the agent-count listener, the wave listener, the
    // defaults path - so a list captured here would go stale the first time the
    // count changes. Walking finds whatever exists when the toggle is flipped.
    //
    // Gated on the SITE fact alone, not on `split`: which room a node stands in
    // is worth recording even when both rooms share one customer network.
    const showRooms = (item: {
      id: string;
      visible?: boolean;
      children?: any;
    }): void => {
      if (item.id?.endsWith("_zone")) {
        (item as { visible: boolean }).visible = twoRoom;
      }
      item.children?.forEach((child: any) => showRooms(child));
    };
    showRooms(networks as unknown as { id: string; children?: any });
  };
  applyZoneVisibility();
  customerZoned.addChangeListener(applyZoneVisibility);
  twoRoomQuestion?.addChangeListener(applyZoneVisibility);

  const customerNetworkNtp = new OpenQuestion("customer_ntp", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.ntp"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.ntp"),
    ),
  ]);
  questionsRights(customerNetworkNtp);

  customerNetworkNtp.validator = new TextValidator(TextValidatorType.IP);
  customerNetwork.addChild(customerNetworkNtp);

  const customerNetworkDns = new OpenQuestion("customer_dns", [
    new Translation(
      "en",
      getTranslation("en", "anketa.documentation.opsanio.network.dns"),
    ),
    new Translation(
      "de",
      getTranslation("de", "anketa.documentation.opsanio.network.dns"),
    ),
  ]);
  customerNetworkDns.validator = new TextValidator(TextValidatorType.IP);
  customerNetwork.addChild(customerNetworkDns);

  // Repeatable list of static DNS records (name / type / ip). Stored as a
  // JSON-stringified array; parsed back into OpsanioNetwork.dnsEntries for the
  // opsanio values (see getCustomerNetwork in helper/parser/opsanio.ts).
  const customerDnsEntries = new ComplexQuestion(
    "customer_dns_entries",
    [
      new Translation(
        "en",
        getTranslation(
          "en",
          "anketa.documentation.opsanio.network.dns.entries.title",
        ),
      ),
      new Translation(
        "de",
        getTranslation(
          "de",
          "anketa.documentation.opsanio.network.dns.entries.title",
        ),
      ),
    ],
    "OpsanioDnsEntries",
  );
  customerDnsEntries.description = [
    new Translation(
      "en",
      getTranslation(
        "en",
        "anketa.documentation.opsanio.network.dns.entries.description",
      ),
    ),
    new Translation(
      "de",
      getTranslation(
        "de",
        "anketa.documentation.opsanio.network.dns.entries.description",
      ),
    ),
  ];
  questionsRights(customerDnsEntries);
  customerNetwork.addChild(customerDnsEntries);

  // Re-generates the Probes section. Probe CPE/Transfer IPs are allocated from
  // the top of the CPE (A/B) and Transfer CIDRs downwards (agents allocate from
  // the bottom up), so probes must be regenerated whenever those CIDRs change.
  const regenerateProbes = () => {
    const countProbes: number = parseInt(String(opsanioProbes.fact.raw), 10);
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";
    networks.addChild(
      generateProbes(
        countProbes,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
      ),
    );
  };

  exanioName.addChangeListener(() => {
    const exanioNamePlain = exanioName.fact.raw;
    if (exanioNamePlain) {
      wireguardDNS.setFactValue(`mekan.${exanioNamePlain}.exanio.net`);
    }

    const count: number = parseInt(String(opsanioAgents.fact.raw), 10);
    const countServers: number = parseInt(
      String(opsanioUpdateServer.fact.raw),
      10,
    );
    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";

    const exanioCidr: string = exanioCloudNetwork.fact.raw
      ? exanioCloudNetwork.fact.raw.toString()
      : "";
    const customerGw: string = customerNetworkGw.fact.raw
      ? customerNetworkGw.fact.raw.toString()
      : "";

    networks.addChild(
      generateAgents(
        count,
        vpcName,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
        setDefaultValues,
        defaultValues,
        customerGw,
        bgpEnabled,
        isTwoRoomSite(),
      ),
    );
    networks.addChild(
      generateKubernetesServers(
        countServers,
        vpcName,
        exanioCidr,
        questionsRights,
      ),
    );
    networks.addChild(
      generateKula(vpcName, exanioCidr, transferCidr, questionsRights),
    );
    regenerateProbes();
  });

  exanioNetworkSelection.addChangeListener(() => {
    const vpc: Vpc | undefined =
      exanioNetworkSelection.fact.raw !== undefined
        ? JSON.parse(exanioNetworkSelection.fact.raw as string)
        : undefined;

    if (vpc) {
      const exanioNetworkIPv4 = vpc.cidr;
      const exanioMainVlan: string = vpc.tenantId.toString();
      if (exanioMainVlan) {
        exanioCloudVlan.setFactValue(exanioMainVlan);
        transferVlan.setFactValue(exanioMainVlan);
        wireguardPort.setFactValue((vpc.tenantId + 2000).toString());

        if (exanioNetworkIPv4) {
          try {
            const subnets = splitSubnets(exanioNetworkIPv4, 3);
            exanioCloudNetwork.setFactValue(subnets[0]);
            transferNetwork.setFactValue(subnets[1]);
            cpeNetwork.setFactValue(subnets[2]);
          } catch (error) {
            console.error("Error Calc sub", error.message);
          }
        }
      }
    }
  });

  cpeNetwork.addChangeListener(() => {
    const cpeNetworkIPv4 = cpeNetwork.fact.raw.toString();
    if (cpeNetworkIPv4) {
      try {
        const subnets = splitSubnets(cpeNetworkIPv4, 2);
        cpeNetworkA.setFactValue(subnets[0]);
        cpeNetworkB.setFactValue(subnets[1]);
      } catch (error) {
        console.error(error.message);
      }
    }

    const count: number = parseInt(String(opsanioAgents.fact.raw), 10);
    const countServers: number = parseInt(
      String(opsanioUpdateServer.fact.raw),
      10,
    );
    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";

    const exanioCidr: string = exanioCloudNetwork.fact.raw
      ? exanioCloudNetwork.fact.raw.toString()
      : "";
    const customerGw: string = customerNetworkGw.fact.raw
      ? customerNetworkGw.fact.raw.toString()
      : "";

    networks.addChild(
      generateAgents(
        count,
        vpcName,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
        setDefaultValues,
        defaultValues,
        customerGw,
        bgpEnabled,
        isTwoRoomSite(),
      ),
    );
    networks.addChild(
      generateKubernetesServers(
        countServers,
        vpcName,
        exanioCidr,
        questionsRights,
      ),
    );
    networks.addChild(
      generateKula(vpcName, exanioCidr, transferCidr, questionsRights),
    );
    regenerateProbes();
  });

  transferNetwork.addChangeListener(() => {
    const count: number = parseInt(String(opsanioAgents.fact.raw), 10);

    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";

    const exanioCidr: string = exanioCloudNetwork.fact.raw
      ? exanioCloudNetwork.fact.raw.toString()
      : "";
    const customerGw: string = customerNetworkGw.fact.raw
      ? customerNetworkGw.fact.raw.toString()
      : "";

    networks.addChild(
      generateAgents(
        count,
        vpcName,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
        setDefaultValues,
        defaultValues,
        customerGw,
        bgpEnabled,
        isTwoRoomSite(),
      ),
    );
    networks.addChild(
      generateKula(vpcName, exanioCidr, transferCidr, questionsRights),
    );
    regenerateProbes();
  });
  customerNetworkCidr.addChangeListener(() => {
    const count: number = parseInt(String(opsanioAgents.fact.raw), 10);
    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";
    const customerGw: string = customerNetworkGw.fact.raw
      ? customerNetworkGw.fact.raw.toString()
      : "";

    networks.addChild(
      generateAgents(
        count,
        vpcName,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
        setDefaultValues,
        defaultValues,
        customerGw,
        bgpEnabled,
        isTwoRoomSite(),
      ),
    );
    regenerateProbes();
  });
  opsanioAgents.addChangeListener(() => {
    const count: number = parseInt(String(opsanioAgents.fact.raw), 10);
    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const cpeCidrA: string = cpeNetworkA.fact.raw
      ? cpeNetworkA.fact.raw.toString()
      : "";
    const cpeCidrB: string = cpeNetworkB.fact.raw
      ? cpeNetworkB.fact.raw.toString()
      : "";
    const transferCidr: string = transferNetwork.fact.raw
      ? transferNetwork.fact.raw.toString()
      : "";
    const customerGw: string = customerNetworkGw.fact.raw
      ? customerNetworkGw.fact.raw.toString()
      : "";

    networks.addChild(
      generateAgents(
        count,
        vpcName,
        cpeCidrA,
        cpeCidrB,
        transferCidr,
        questionsRights,
        oldValues,
        setDefaultValues,
        defaultValues,
        customerGw,
        bgpEnabled,
        isTwoRoomSite(),
      ),
    );
    regenerateProbes();
  });

  opsanioUpdateServer.addChangeListener(() => {
    const countServers: number = parseInt(
      String(opsanioUpdateServer.fact.raw),
      10,
    );
    const vpcName: string = exanioName.fact.raw
      ? exanioName.fact.raw.toString()
      : "";
    const exanioCidr: string = exanioCloudNetwork.fact.raw
      ? exanioCloudNetwork.fact.raw.toString()
      : "";
    networks.addChild(
      generateKubernetesServers(
        countServers,
        vpcName,
        exanioCidr,
        questionsRights,
      ),
    );
  });

  opsanioProbes.addChangeListener(() => {
    regenerateProbes();
  });

  const count: number = parseInt(String(opsanioAgents.fact.raw), 10);
  const vpcName: string = exanioName.fact.raw
    ? exanioName.fact.raw.toString()
    : "";
  const cpeCidrA: string = cpeNetworkA.fact.raw
    ? cpeNetworkA.fact.raw.toString()
    : "";
  const cpeCidrB: string = cpeNetworkB.fact.raw
    ? cpeNetworkB.fact.raw.toString()
    : "";
  const transferCidr: string = transferNetwork.fact.raw
    ? transferNetwork.fact.raw.toString()
    : "";
  const customerGw: string = customerNetworkGw.fact.raw
    ? customerNetworkGw.fact.raw.toString()
    : "";

  networks.addChild(
    generateAgents(
      count,
      vpcName,
      cpeCidrA,
      cpeCidrB,
      transferCidr,
      questionsRights,
      oldValues,
      setDefaultValues,
      defaultValues,
      customerGw,
      bgpEnabled,
      // The INITIAL render needs this as much as the change listeners do. Without
      // it a two-room site comes up with the room field hidden and the CPE halves
      // still handed out by index parity, and only starts behaving once some
      // unrelated field is touched.
      isTwoRoomSite(),
    ),
  );

  regenerateProbes();

  const countServers: number = parseInt(
    String(opsanioUpdateServer.fact.raw),
    10,
  );
  const exanioCidr: string = exanioCloudNetwork.fact.raw
    ? exanioCloudNetwork.fact.raw.toString()
    : "";
  networks.addChild(
    generateKubernetesServers(
      countServers,
      vpcName,
      exanioCidr,
      questionsRights,
    ),
  );
  networks.addChild(
    generateKula(vpcName, exanioCidr, transferCidr, questionsRights),
  );
  if (setDefaultValues) {
    size.setFactValue("small");
    serviceModel.setFactValue("medium");
    isVm.setFactValue(1);
    useUefi.setFactValue("0");
    opsanioUpdateServer.setFactValue(1);
    opsanioAgents.setFactValue(defaultValues.agents.length);
    exanioNetworkSelection.setFactValue(
      JSON.stringify(defaultValues.vpc.vpcs[0]),
    );
    exanioName.setFactValue(defaultValues.vpcName);
    // The domain the cluster is on before any customer certificate exists -
    // a bare domain, not a URL, because that is what the answer means now.
    customerDnsName.setFactValue(`${defaultValues.vpcName}.exanio.net`);
    wave.setFactValue(defaultValues.nextWave);
  }
  return networks;
};
